"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DropdownTrigger = void 0;
exports.useDropdownTrigger = useDropdownTrigger;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _mergeRefs = _interopRequireDefault(require("merge-refs"));
var _isRightClick = _interopRequireDefault(require("@ux/component-utilities/is-right-click"));
var _composeEventHandlers = _interopRequireDefault(require("@ux/component-utilities/compose-event-handlers"));
var _dropdown = require("./dropdown");
const _excluded = ["onKeyDown", "onMouseDown", "id", "ref"],
  _excluded2 = ["as"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @type TriggerHook
 * @property {object} data Derived data from Dropdown state
 * @property {boolean} data.isExpanded Is the menu open or closed
 * @property {string} data.controls HTMLElement id for the list element
 * @property {React.ForwardedRef} props.ref Composed Ref
 * @property {object} props Props to be spread onto element
 * @property {string} props.id HTMLElement id
 * @property {function} props.onKeyDown Composed Keydown event handler
 * @property {function} props.onMouseDown Composed Mousedown event handler
 */
/**
 * Trigger element for opening/closing the dropdown. The trigger is the focus point when the menu is closed
 * @param {Object} props useDropdownTrigger props
 * @param {function} props.onKeyDown Keydown event handler
 * @param {function} props.onMouseDown Mousedown event handler
 * @param {string} props.id HTMLElement id to be used for trigger
 * @param {React.ForwardedRef} props.ref Forwarded Ref
 * @returns {TriggerHook} Data used to implement dropdown trigger
*/
function useDropdownTrigger(_ref) {
  let {
      onKeyDown,
      onMouseDown,
      id,
      ref: forwardedRef
    } = _ref,
    props = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    dispatch,
    listId,
    mouseDownStartPosRef,
    triggerRef,
    state: {
      triggerId,
      isExpanded
    }
  } = (0, _dropdown.useDropdownContext)('useDropdownTrigger');
  const ref = (0, _mergeRefs.default)(triggerRef, forwardedRef);
  const items = (0, _dropdown.useDropdownDescendants)();
  const firstNonDisabledIndex = _react.default.useMemo(() => items.findIndex(item => !item.disabled), [items]);
  // findLastIndex is only supported in Node 18, using this instead
  const lastNonDisabledIndex = _react.default.useMemo(() => {
    return items.lastIndexOf([...items].reverse().find(item => !item.disabled));
  }, [items]);

  // Update triggerId in state if it is passed in with props
  _react.default.useEffect(() => {
    if (id != null && id !== triggerId) {
      dispatch({
        type: _dropdown.SET_BUTTON_ID,
        payload: id
      });
    }
  }, [triggerId, dispatch, id]);
  _react.default.useEffect(() => {
    if (id == null) {
      dispatch({
        type: _dropdown.SET_BUTTON_ID,
        payload: triggerId
      });
    }
  }, [triggerId, dispatch, id]);
  function handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault(); // prevent scroll
        dispatch({
          type: _dropdown.OPEN_MENU_AT_INDEX,
          payload: {
            index: lastNonDisabledIndex
          }
        });
        break;
      case 'ArrowDown':
        event.preventDefault(); // prevent scroll
        dispatch({
          type: _dropdown.OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
      case 'Enter':
      case ' ':
        dispatch({
          type: _dropdown.OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
      default:
        break;
    }
  }
  function handleMouseDown(event) {
    if ((0, _isRightClick.default)(event.nativeEvent)) {
      return;
    }
    mouseDownStartPosRef.current = {
      x: event.clientX,
      y: event.clientY
    };
    if (isExpanded) {
      dispatch({
        type: _dropdown.CLOSE_MENU,
        payload: {
          triggerRef
        }
      });
    } else {
      dispatch({
        type: _dropdown.OPEN_MENU_CLEARED
      });
    }
  }
  return {
    data: {
      isExpanded,
      controls: listId
    },
    props: _objectSpread(_objectSpread({}, props), {}, {
      ref,
      id: triggerId || null,
      onKeyDown: (0, _composeEventHandlers.default)(onKeyDown, handleKeyDown),
      onMouseDown: (0, _composeEventHandlers.default)(onMouseDown, handleMouseDown)
    })
  };
}
const DropdownTrigger = exports.DropdownTrigger = /*#__PURE__*/_react.default.forwardRef((_ref2, forwardedRef) => {
  let {
      as: Comp = 'button'
    } = _ref2,
    rest = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
  const {
    props
  } = useDropdownTrigger(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /*#__PURE__*/_react.default.createElement(Comp, props);
});
DropdownTrigger.propTypes = {
  as: _propTypes.default.node
};
DropdownTrigger.displayName = 'DropdownTrigger';